import type { Middleware } from '@reduxjs/toolkit'
import type { MessageEngine } from '@/engine/MessageEngine/MessageEngine'
import {
  add as addMessage,
  set as setMessages,
  update as updateMessage,
  _updateById as updateById,
  updateWithBreak,
  clear as clearMessages,
  _remove as removeMessage,
  _removeMany as removeManyMessages,
  _readMessagesInChat
} from '../messages/messages'
import { setActiveChatId } from '../chats/chats'
import { setNews, _changeNewsViewed, _readAllNews } from '../news/news'
import { setUser } from '../user/user'
import { createActionHandler } from './utils'

interface Deps {
  messageEngine: MessageEngine
}

export const createMessageMiddleware = ({ messageEngine }: Deps): Middleware => {
  let actions: ((action: unknown) => void)[] | null = null

  const ensureActions = () => {
    if (!actions) {
      actions = [
        createActionHandler({
          action: addMessage,
          getKey: ({ jid, message }) => `${jid}:${message.id}`,
          onProcess: messageEngine.onAddMessage
        }),
        createActionHandler({
          action: setMessages,
          mode: 'last',
          onProcess: messageEngine.onSetMessages
        }),
        createActionHandler({
          action: removeMessage,
          getKey: ({ jid, messageId }) => `${jid}:${messageId}`,
          onProcess: messageEngine.onRemoveMessage
        }),
        createActionHandler({
          action: removeManyMessages,
          mode: 'all',
          onProcess: messageEngine.onRemoveMessages
        }),
        createActionHandler({
          action: updateMessage,
          mode: 'all',
          onProcess: messageEngine.onUpdateMessage
        }),
        createActionHandler({
          action: updateById,
          mode: 'all',
          onProcess: messageEngine.onUpdateById
        }),
        createActionHandler({
          action: updateWithBreak,
          mode: 'all',
          onProcess: messageEngine.onUpdateWithBreak
        }),
        createActionHandler({
          action: clearMessages,
          mode: 'last',
          onProcess: messageEngine.onClearMessages
        }),
        createActionHandler({
          action: setUser,
          mode: 'last',
          onProcess: messageEngine.onSetUser
        }),
        createActionHandler({
          action: setActiveChatId,
          mode: 'last',
          onProcess: messageEngine.onSetActiveChatId
        }),
        createActionHandler({
          action: setNews,
          mode: 'last',
          onProcess: messageEngine.onSetNews
        }),
        createActionHandler({
          action: _changeNewsViewed,
          getKey: ({ id }) => id,
          onProcess: messageEngine.onChangeNewsViewed
        }),
        createActionHandler({
          action: _readAllNews,
          mode: 'last',
          onProcess: messageEngine.onReadAllNews
        }),
        createActionHandler({
          action: _readMessagesInChat,
          mode: 'last',
          onProcess: messageEngine.onReadAllMessages
        })
      ]
    }
    return actions
  }

  return _store => next => action => {
    const result = next(action)

    ensureActions().forEach(run => {
      run(action)
    })

    return result
  }
}
